// src/components/UnitDetail.jsx

import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const UnitDetail = ({ unit, onBack = () => {}, onOpenSensorMap = () => {} }) => {
  const { t, language } = useLanguage();
  const locale = language === 'tr' ? 'tr-TR' : 'en-US';

  const formatDateTime = (date) => {
    if (!date) {
      return t('units.table.noData');
    }
    return new Date(date).toLocaleString(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'UTC'
    });
  };

  if (!unit) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm">
        <p className="text-lg font-semibold text-slate-900">{t('units.detail.emptyTitle')}</p>
        <p className="mt-2 text-sm text-slate-600">{t('units.detail.emptyDescription')}</p>
        <button
          type="button"
          onClick={onBack}
          className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
        >
          {t('units.detail.back')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            {t('units.detail.subtitle')}
          </p>
          <h2 className="text-2xl font-bold text-slate-900">{unit.unitName}</h2>
          <p className="mt-1 text-sm text-slate-600">
            {t('units.detail.locationLabel')}: {unit.location}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
          >
            {t('units.detail.back')}
          </button>
          <button
            type="button"
            onClick={() => onOpenSensorMap(unit.location)}
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            {t('units.actions.sensorMap')}
          </button>
        </div>
      </header>

      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            {t('units.detail.summary')}
          </h3>
          <dl className="mt-4 space-y-3 text-sm text-slate-700">
            <div className="flex items-center justify-between">
              <dt className="font-medium text-slate-600">{t('units.table.dateTime')}</dt>
              <dd className="font-semibold text-slate-900">{formatDateTime(unit.timestamp)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="font-medium text-slate-600">{t('units.table.tempHumidity')}</dt>
              <dd className="font-semibold text-slate-900">
                {unit.temperature}
                {t('units.celsius')} / {unit.humidity}
                {t('units.percent')}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="font-medium text-slate-600">{t('units.table.status')}</dt>
              <dd>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                    unit.status === 'attention'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-emerald-100 text-emerald-700'
                  }`}
                >
                  {t(`units.status.${unit.status}`)}
                </span>
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            {t('units.detail.fileInfo')}
          </h3>
          <dl className="mt-4 space-y-3 text-sm text-slate-700">
            <div className="flex items-center justify-between">
              <dt className="font-medium text-slate-600">{t('units.detail.fileName')}</dt>
              <dd className="font-semibold text-slate-900">{unit.fileName}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="font-medium text-slate-600">{t('units.table.unit')}</dt>
              <dd className="font-semibold text-slate-900">{unit.unitName}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="font-medium text-slate-600">{t('units.table.location')}</dt>
              <dd className="font-semibold text-slate-900">{unit.location}</dd>
            </div>
          </dl>
        </div>
      </section>
    </div>
  );
};

export default UnitDetail;
